import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import * as Sentry from "@sentry/react";
import api from "../data/axios/api";

export interface DownloadWarning {
  name: string;
  email: string;
  missing_fields: string[];
}

export interface DownloadJob {
  id: string;
  label: string;
  status: "pending" | "done" | "error" | "empty";
  signedUrl?: string;
  filename?: string;
  startedAt: number;
  jobEndpointPath?: string;
  warnings?: DownloadWarning[];
}

interface DownloadManagerContextValue {
  jobs: DownloadJob[];
  startDownload: (
    params: Record<string, string | string[]>,
    label: string,
    starterPackBaseUrl: string,
    jobEndpointPath?: string,
  ) => Promise<void>;
  dismissJob: (id: string) => void;
  dismissAll: () => void;
}

const DownloadManagerContext = createContext<DownloadManagerContextValue | null>(null);

const STORAGE_KEY = "gg_hub_download_jobs";
const POLL_MS = 2000;

const IN_PROGRESS_STATUSES = new Set([
  "pending",
  "running",
  "processing",
  "queued",
  "in_progress",
  "started",
]);

const MAX_POLL_DURATION_MS = 10 * 60 * 1000; // 10 minutos

const MAX_CONSECUTIVE_POLL_ERRORS = 3;

const loadFromStorage = (): DownloadJob[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const jobs: DownloadJob[] = JSON.parse(raw);
    return jobs.filter((j) => j.status === "done" || j.status === "error");
  } catch {
    return [];
  }
};

const saveToStorage = (jobs: DownloadJob[]) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(jobs.filter((j) => j.status === "done" || j.status === "error")),
    );
  } catch {}
};

export const DownloadManagerProvider = ({ children }: { children: ReactNode }) => {
  const [jobs, setJobs] = useState<DownloadJob[]>(loadFromStorage);
  const jobsRef = useRef<DownloadJob[]>(jobs);
  const baseUrlsRef = useRef<Map<string, string>>(new Map());
  const consecutiveErrorsRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  const updateJob = useCallback((id: string, update: Partial<DownloadJob>) => {
    setJobs((prev) => {
      const next = prev.map((j) => (j.id === id ? { ...j, ...update } : j));
      saveToStorage(next);
      return next;
    });
  }, []);

  useEffect(() => {
    const interval = setInterval(async () => {
      const pending = jobsRef.current.filter((j) => j.status === "pending");
      if (pending.length === 0) return;

      for (const job of pending) {
        const baseUrl = baseUrlsRef.current.get(job.id);
        if (!baseUrl) continue;

        if (Date.now() - job.startedAt > MAX_POLL_DURATION_MS) {
          Sentry.captureMessage("Download job excedeu tempo máximo", { level: "warning", extra: { job_id: job.id } });
          consecutiveErrorsRef.current.delete(job.id);
          updateJob(job.id, { status: "error" });
          continue;
        }

        const pollPath = job.jobEndpointPath ?? "onboarding/candidates/export/files/jobs";
        try {
          const res = await api.post("/api/v1/proxy", {
            url: `${baseUrl}/api/v1/admin/${pollPath}/${job.id}`,
            method: "GET",
          });
          const data = res.data as {
            status?: string;
            signed_url?: string;
            filename?: string;
            warnings?: DownloadWarning[];
          } | undefined;

          consecutiveErrorsRef.current.delete(job.id);

          const status = (data?.status ?? "").toLowerCase();
          const signedUrl = data?.signed_url;

          if (status === "done" || (signedUrl && (status === "completed" || status === "ready"))) {
            updateJob(job.id, {
              status: "done",
              signedUrl,
              filename: data?.filename,
              warnings: data?.warnings,
            });
          } else if (status === "empty") {
            updateJob(job.id, { status: "empty", warnings: data?.warnings });
          } else if (status === "error" || status === "failed" || status === "canceled" || status === "cancelled") {
            Sentry.captureMessage("Download job terminou com status de falha", { level: "error", extra: { job_id: job.id, status: data?.status } });
            updateJob(job.id, { status: "error" });
          } else if (!IN_PROGRESS_STATUSES.has(status)) {
            Sentry.captureMessage("Download job retornou status não reconhecido", { level: "warning", extra: { job_id: job.id, status: data?.status } });
            updateJob(job.id, { status: "error" });
          }
        } catch (err) {
          const count = (consecutiveErrorsRef.current.get(job.id) ?? 0) + 1;
          Sentry.captureException(err, { extra: { job_id: job.id, attempt: count }, tags: { flow: "download-poll" } });
          if (count >= MAX_CONSECUTIVE_POLL_ERRORS) {
            consecutiveErrorsRef.current.delete(job.id);
            updateJob(job.id, { status: "error" });
          } else {
            consecutiveErrorsRef.current.set(job.id, count);
          }
        }
      }
    }, POLL_MS);

    return () => clearInterval(interval);
  }, [updateJob]);

  const startDownload = useCallback(
    async (
      params: Record<string, string | string[]>,
      label: string,
      starterPackBaseUrl: string,
      jobEndpointPath = "onboarding/candidates/export/files/jobs",
    ) => {
      try {
        const urlParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((v) => urlParams.append(key, v));
          } else if (value !== undefined && value !== null) {
            urlParams.append(key, value.toString());
          }
        });
        const qs = urlParams.toString();
        const res = await api.post("/api/v1/proxy", {
          url: `${starterPackBaseUrl}/api/v1/admin/${jobEndpointPath}?${qs}`,
          method: "POST",
        });
        const { job_id } = (res.data ?? {}) as { job_id?: string };

        if (!job_id || typeof job_id !== "string") {
          Sentry.captureMessage("Download: upstream não retornou job_id", { level: "error", extra: { response_data: res.data } });
          throw new Error(
            "Não foi possível iniciar o download. Tente selecionar menos candidatos.",
          );
        }

        const newJob: DownloadJob = {
          id: job_id,
          label,
          status: "pending",
          startedAt: Date.now(),
          jobEndpointPath,
        };
        baseUrlsRef.current.set(job_id, starterPackBaseUrl);
        setJobs((prev) => [newJob, ...prev]);
      } catch (err) {
        if (err instanceof Error && err.message) throw err;
        throw new Error("Não foi possível iniciar o download.");
      }
    },
    [],
  );

  const dismissJob = useCallback((id: string) => {
    setJobs((prev) => {
      const next = prev.filter((j) => j.id !== id);
      saveToStorage(next);
      return next;
    });
    baseUrlsRef.current.delete(id);
    consecutiveErrorsRef.current.delete(id);
  }, []);

  const dismissAll = useCallback(() => {
    setJobs((prev) => {
      const next = prev.filter((j) => j.status === "pending");
      saveToStorage(next);
      return next;
    });
  }, []);

  return (
    <DownloadManagerContext.Provider value={{ jobs, startDownload, dismissJob, dismissAll }}>
      {children}
    </DownloadManagerContext.Provider>
  );
};

export const useDownloadManager = (): DownloadManagerContextValue => {
  const ctx = useContext(DownloadManagerContext);
  if (!ctx) throw new Error("useDownloadManager must be used within DownloadManagerProvider");
  return ctx;
};
